import { createContext, useContext, useReducer, useCallback, useEffect, useRef, ReactNode } from 'react';
import type { EntranceConnection, EntranceCategory, EntranceTrackerState, ShopItem } from '../data/entrance-types';
import { ALL_CATEGORIES, ALL_ZONES } from '../data/entrance-types';
import { ALL_VANILLA_ENTRANCES } from '../data/vanilla-entrances';

// ─── Actions ───────────────────────────────────────────────────

type EntranceAction =
  | { type: 'TRACK_ENTRANCE'; id: string; destination: string; from?: string | null; linkedTo?: string | null; isReverse?: boolean }
  | { type: 'UNTRACK_ENTRANCE'; id: string }
  | { type: 'IMPORT_SEED'; data: Record<string, string | { region: string; from: string }> }
  | { type: 'SET_VISIBLE_CATEGORIES'; categories: EntranceCategory[] }
  | { type: 'TOGGLE_CATEGORY'; category: EntranceCategory }
  | { type: 'TOGGLE_ZONE'; zone: string }
  | { type: 'SET_SEARCH'; term: string }
  | { type: 'SET_VIEW_MODE'; mode: 'graph' | 'list' }
  | { type: 'TOGGLE_DECOUPLED' }
  | { type: 'SET_NOTE'; id: string; note: string }
  | { type: 'TOGGLE_ZONE_REWARD'; zone: string; rewardId: string }
  | { type: 'SET_SHOP_DATA'; entranceId: string; items: ShopItem[] }
  | { type: 'SET_SCRUBS_COUNT'; entranceId: string; count: number }
  | { type: 'TOGGLE_HIDE_DONE' }
  | { type: 'RESET' };

// ─── Initial State ─────────────────────────────────────────────

// Entrances that are auto-tracked to a dead end on init
const AUTO_TRACKED: Record<string, string> = {
  'Potion Shop::Potion Shop': 'Shop',
};

function buildInitialConnections(): Record<string, EntranceConnection> {
  const connections: Record<string, EntranceConnection> = {};
  for (const ent of ALL_VANILLA_ENTRANCES) {
    const autoDestination = AUTO_TRACKED[ent.id] ?? null;
    connections[ent.id] = {
      id: ent.id,
      zone: ent.zone,
      vanillaTo: ent.vanillaTo,
      category: ent.category,
      label: ent.label,
      actualDestination: autoDestination,
      actualFrom: null,
      linkedTo: null,
      isReverse: false,
      isTracked: autoDestination !== null,
    };
  }
  return connections;
}

const initialState: EntranceTrackerState = {
  connections: buildInitialConnections(),
  visibleCategories: [...ALL_CATEGORIES],
  visibleZones: [...ALL_ZONES],
  searchTerm: '',
  viewMode: 'graph',
  isDecoupled: true,
  hideDone: false,
  zoneRewards: {},
  shopData: {},
  scrubsCount: {},
};

// ─── Reducer ───────────────────────────────────────────────────

function entranceReducer(state: EntranceTrackerState, action: EntranceAction): EntranceTrackerState {
  switch (action.type) {
    case 'TRACK_ENTRANCE': {
      const conn = state.connections[action.id];
      if (!conn) return state;
      return {
        ...state,
        connections: {
          ...state.connections,
          [action.id]: {
            ...conn,
            actualDestination: action.destination,
            actualFrom: action.from ?? null,
            linkedTo: action.linkedTo ?? null,
            isReverse: action.isReverse ?? false,
            isTracked: true,
          },
        },
      };
    }

    case 'UNTRACK_ENTRANCE': {
      const conn = state.connections[action.id];
      if (!conn) return state;
      const newConnections = {
        ...state.connections,
        [action.id]: {
          ...conn,
          actualDestination: null,
          actualFrom: null,
          linkedTo: null,
          isReverse: false,
          isTracked: false,
        },
      };
      // Also untrack the linked reverse side
      if (conn.linkedTo && newConnections[conn.linkedTo]) {
        newConnections[conn.linkedTo] = {
          ...newConnections[conn.linkedTo],
          actualDestination: null,
          actualFrom: null,
          linkedTo: null,
          isReverse: false,
          isTracked: false,
        };
      }
      return { ...state, connections: newConnections };
    }

    case 'IMPORT_SEED': {
      // For seed import, we just store all entries as-is keyed by the seed key.
      // The seed JSON keys are the canonical OoTR entrance names.
      // We create connections dynamically from the seed data.
      const newConnections = { ...state.connections };

      for (const [seedKey, value] of Object.entries(action.data)) {
        // Store each seed entry as a connection using the seed key as ID
        const destination = typeof value === 'string' ? value : value.region;
        const from = typeof value === 'string' ? null : value.from;

        // Try to match to an existing connection first
        const matchingId = Object.keys(newConnections).find(id => {
          const conn = newConnections[id];
          return conn.label === seedKey || `${conn.zone}::${conn.label}` === seedKey;
        });

        if (matchingId) {
          newConnections[matchingId] = {
            ...newConnections[matchingId],
            actualDestination: destination,
            actualFrom: from,
            isTracked: true,
          };
        } else {
          // Create a new connection for unmatched seed entries
          // Parse the seed key "Region -> Target" format
          const parts = seedKey.split(' -> ');
          const zone = parts.length > 0 ? (parts[0].length <= 4 ? parts[0] : 'HF') : 'HF';
          const category = seedKey.includes('Warp') ? 'warp' as const
            : seedKey.includes('Spawn') ? 'spawn' as const
            : seedKey.includes('Owl') ? 'owl' as const
            : seedKey.includes('Boss') ? 'boss' as const
            : 'overworld' as const;

          newConnections[`seed::${seedKey}`] = {
            id: `seed::${seedKey}`,
            zone,
            vanillaTo: parts[1] ?? seedKey,
            category,
            label: seedKey,
            actualDestination: destination,
            actualFrom: from,
            linkedTo: null,
            isReverse: false,
            isTracked: true,
          };
        }
      }

      return { ...state, connections: newConnections };
    }

    case 'SET_VISIBLE_CATEGORIES':
      return { ...state, visibleCategories: action.categories };

    case 'TOGGLE_CATEGORY': {
      const categories = state.visibleCategories.includes(action.category)
        ? state.visibleCategories.filter(c => c !== action.category)
        : [...state.visibleCategories, action.category];
      return { ...state, visibleCategories: categories };
    }

    case 'TOGGLE_ZONE': {
      const zones = state.visibleZones.includes(action.zone)
        ? state.visibleZones.filter(z => z !== action.zone)
        : [...state.visibleZones, action.zone];
      return { ...state, visibleZones: zones };
    }

    case 'SET_SEARCH':
      return { ...state, searchTerm: action.term };

    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.mode };

    case 'TOGGLE_DECOUPLED':
      return { ...state, isDecoupled: !state.isDecoupled };

    case 'SET_NOTE': {
      const conn = state.connections[action.id];
      if (!conn) return state;
      return {
        ...state,
        connections: {
          ...state.connections,
          [action.id]: { ...conn, note: action.note || undefined },
        },
      };
    }

    case 'TOGGLE_ZONE_REWARD': {
      const { zone, rewardId } = action;
      const prev = state.zoneRewards;
      const currentZoneRewards = prev[zone] ?? [];
      const isAssignedHere = currentZoneRewards.includes(rewardId);

      // Remove reward from any other zone it's currently in
      const cleaned: Record<string, string[]> = {};
      for (const [z, rewards] of Object.entries(prev)) {
        const filtered = rewards.filter(r => r !== rewardId);
        if (filtered.length > 0) cleaned[z] = filtered;
      }

      // Toggle: if it was on this zone, it's now removed; otherwise add it
      if (!isAssignedHere) {
        cleaned[zone] = [...(cleaned[zone] ?? []), rewardId];
      }

      return { ...state, zoneRewards: cleaned };
    }

    case 'SET_SHOP_DATA': {
      const filtered = action.items.filter(i => i.itemName !== null || i.price !== null);
      const newShopData = { ...state.shopData };
      if (filtered.length > 0) {
        newShopData[action.entranceId] = action.items;
      } else {
        delete newShopData[action.entranceId];
      }
      return { ...state, shopData: newShopData };
    }

    case 'SET_SCRUBS_COUNT': {
      const newCounts = { ...state.scrubsCount };
      if (action.count > 0) {
        newCounts[action.entranceId] = action.count;
      } else {
        delete newCounts[action.entranceId];
      }
      return { ...state, scrubsCount: newCounts };
    }

    case 'TOGGLE_HIDE_DONE':
      return { ...state, hideDone: !state.hideDone };

    case 'RESET':
      return { ...initialState, connections: buildInitialConnections() };

    default:
      return state;
  }
}

// ─── Persistence ───────────────────────────────────────────────

const STORAGE_KEY = 'entrance-tracker-state';

function serializeState(state: EntranceTrackerState): string {
  return JSON.stringify({
    ...state,
    // Only persist tracked connections to save space
    connections: Object.fromEntries(
      Object.entries(state.connections).filter(([, c]) => c.isTracked)
    ),
    zoneRewards: state.zoneRewards,
    shopData: state.shopData,
    scrubsCount: state.scrubsCount,
  });
}

function loadState(): EntranceTrackerState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return { ...initialState, connections: buildInitialConnections() };

    const parsed = JSON.parse(stored);
    const connections = buildInitialConnections();

    // Merge tracked connections from storage
    if (parsed.connections) {
      for (const [id, saved] of Object.entries(parsed.connections)) {
        if (connections[id]) {
          connections[id] = { ...connections[id], ...(saved as Partial<EntranceConnection>) };
        }
      }
    }

    // Merge stored zones/categories with ALL_* to include newly added ones
    const storedZones: string[] = parsed.visibleZones ?? [];
    const storedCategories: EntranceCategory[] = parsed.visibleCategories ?? [];
    const mergedZones = [...new Set([...storedZones, ...ALL_ZONES])];
    const mergedCategories = [...new Set([...storedCategories, ...ALL_CATEGORIES])];

    return {
      ...initialState,
      ...parsed,
      connections,
      visibleCategories: mergedCategories,
      visibleZones: mergedZones,
      hideDone: parsed.hideDone ?? false,
      zoneRewards: parsed.zoneRewards ?? {},
      shopData: parsed.shopData ?? {},
      scrubsCount: parsed.scrubsCount ?? {},
    };
  } catch {
    return { ...initialState, connections: buildInitialConnections() };
  }
}

// ─── Context ───────────────────────────────────────────────────

interface EntranceContextType {
  state: EntranceTrackerState;
  dispatch: React.Dispatch<EntranceAction>;
  trackEntrance: (id: string, destination: string, from?: string | null, linkedTo?: string | null, isReverse?: boolean) => void;
  untrackEntrance: (id: string) => void;
  importSeed: (data: Record<string, string | { region: string; from: string }>) => void;
  toggleCategory: (category: EntranceCategory) => void;
  toggleZone: (zone: string) => void;
  toggleZoneReward: (zone: string, rewardId: string) => void;
  toggleHideDone: () => void;
  setShopData: (entranceId: string, items: ShopItem[]) => void;
  setScrubsCount: (entranceId: string, count: number) => void;
  setSearch: (term: string) => void;
  setViewMode: (mode: 'graph' | 'list') => void;
  reset: () => void;
}

const EntranceContext = createContext<EntranceContextType | null>(null);

export function EntranceProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(entranceReducer, undefined, loadState);

  // Debounced persistence
  const saveTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, serializeState(state));
    }, 500);
    return () => clearTimeout(saveTimeout.current);
  }, [state]);

  const trackEntrance = useCallback((id: string, destination: string, from?: string | null, linkedTo?: string | null, isReverse?: boolean) => {
    dispatch({ type: 'TRACK_ENTRANCE', id, destination, from, linkedTo, isReverse });
  }, []);

  const untrackEntrance = useCallback((id: string) => {
    dispatch({ type: 'UNTRACK_ENTRANCE', id });
  }, []);

  const importSeed = useCallback((data: Record<string, string | { region: string; from: string }>) => {
    dispatch({ type: 'IMPORT_SEED', data });
  }, []);

  const toggleCategory = useCallback((category: EntranceCategory) => {
    dispatch({ type: 'TOGGLE_CATEGORY', category });
  }, []);

  const toggleZone = useCallback((zone: string) => {
    dispatch({ type: 'TOGGLE_ZONE', zone });
  }, []);

  const setSearch = useCallback((term: string) => {
    dispatch({ type: 'SET_SEARCH', term });
  }, []);

  const setViewMode = useCallback((mode: 'graph' | 'list') => {
    dispatch({ type: 'SET_VIEW_MODE', mode });
  }, []);

  const toggleZoneReward = useCallback((zone: string, rewardId: string) => {
    dispatch({ type: 'TOGGLE_ZONE_REWARD', zone, rewardId });
  }, []);

  const toggleHideDone = useCallback(() => {
    dispatch({ type: 'TOGGLE_HIDE_DONE' });
  }, []);

  const setShopData = useCallback((entranceId: string, items: ShopItem[]) => {
    dispatch({ type: 'SET_SHOP_DATA', entranceId, items });
  }, []);

  const setScrubsCount = useCallback((entranceId: string, count: number) => {
    dispatch({ type: 'SET_SCRUBS_COUNT', entranceId, count });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return (
    <EntranceContext.Provider
      value={{
        state,
        dispatch,
        trackEntrance,
        untrackEntrance,
        importSeed,
        toggleCategory,
        toggleZone,
        toggleZoneReward,
        toggleHideDone,
        setShopData,
        setScrubsCount,
        setSearch,
        setViewMode,
        reset,
      }}
    >
      {children}
    </EntranceContext.Provider>
  );
}

export function useEntrance() {
  const context = useContext(EntranceContext);
  if (!context) throw new Error('useEntrance must be used within EntranceProvider');
  return context;
}

export type { EntranceAction };
